import { gql } from '@apollo/client';

import { City, Country } from 'src/global/models';

export type Maybe<T> = T | null;

export type HierarchicalLocation = {
  __typename?: 'HierarchicalLocation';
  administrativeLevelName: string;
  children?: Maybe<Array<HierarchicalLocation>>;
  CountryCode?: Maybe<string>;
  formattedName: string;
  id: string;
  name: string;
  latitude: number;
  level: number;
  longitude: number;
  parent?: Maybe<HierarchicalLocation>;
  parents?: Maybe<Array<HierarchicalLocation>>;
  slug?: Maybe<string>;
  label?: string;
};

export const HierarchicalLocationUtils = {
  /**
   * Builds a location label based on the requested hierarchy levels.
   *
   * The output order strictly follows the `levels` array (not the parent hierarchy),
   * allowing flexible compositions such as [3, 2] → "East, Singapore".
   *
   * Example:
   *  - levels [1, 2, 3] → "Singapore, Singapore, East"
   *  - levels [2, 3] → "Singapore, East"
   *  - levels [3, 2] → "East, Singapore"
   */
  buildLocationLabel: (
    levels: number[],
    location?: HierarchicalLocation | null
  ): string => {
    if (!location) return '';
    const allLocations = [...(location.parents ?? []), location];

    const levelMap = new Map<
      number,
      { name?: string; formattedName?: string }
    >();

    for (const loc of allLocations) {
      levelMap.set(loc.level, loc);
    }

    return levels
      .map((level) => {
        const loc = levelMap.get(level);
        return loc?.formattedName ?? loc?.name ?? '';
      })
      .filter(Boolean)
      .join(', ');
  },
};

export type JobLocationPreferenceType = {
  __typename?: 'JobLocationPreferenceType';
  City: City;
  CityId: number;
  Country: Country;
  CountryCode: string;
  createdAt: string;
  HierarchicalLocation?: Maybe<HierarchicalLocation>;
  id: string;
  LocationId?: Maybe<string>;
  needsVisa?: boolean;
  updatedAt: string;
  UserId: string;
};

export type HierarchicalJobLocationPreferenceInput = {
  LocationId: string;
  needsVisa?: Maybe<boolean>;
};

export type MutationUpdateJobLocationPreferencesArgs = {
  addHierarchicalJobLocationPreferencesInput: Array<HierarchicalJobLocationPreferenceInput>;
  deleteJobLocationPreferencesIds: Array<string>;
};

export type QuerySearchHierarchicalLocationsArgs = {
  countryCode?: Maybe<string>;
  levels?: Maybe<Array<number>>;
  limit?: Maybe<number>;
  searchTerm: string;
  withActiveJobsOnly?: Maybe<boolean>;
};

export type SearchHierarchicalLocationData = {
  searchHierarchicalLocations: Array<HierarchicalLocation>;
};

export type HierarchicalJobLocationPreferencesData = {
  jobLocationPreferences: Array<JobLocationPreferenceType>;
};

export type HierarchicalLocationByIdData = {
  location: Maybe<HierarchicalLocation>;
};

export type UpdateJobLocationPreferencesData = {
  updateJobLocationPreferences: Array<JobLocationPreferenceType>;
};

export const HierarchicalLocationFragment = gql`
  fragment HierarchicalLocationFragment on HierarchicalLocation {
    id
    name
    formattedName
    CountryCode: countryCode
    level
  }
`;

export const LocationParentFragment = gql`
  fragment LocationParentFragment on LocationParent {
    id
    name
    formattedName
    CountryCode: countryCode
    level
  }
`;

export const JobLocationPreferenceFields = gql`
  fragment JobLocationPreferenceFields on JobLocationPreferenceType {
    id
    needsVisa
    CountryCode
    CityId
    UserId
    createdAt
    updatedAt
    LocationId
    City {
      id
      name
      CountryCode
    }
    Country {
      code
      name
      demonym
      currency
      languages {
        key
        value
      }
      callingCode
    }
    HierarchicalLocation {
      id
      latitude
      longitude
      CountryCode: countryCode
      name
      administrativeLevelName
      formattedName
      parents {
        id
        name
        level
        administrativeLevelName
        formattedName
      }
    }
  }
`;

export const searchHierarchicalLocation = gql`
  query searchHierarchicalLocations(
    $searchTerm: String!
    $levels: [PositiveInt!]
    $limit: PositiveInt
    $withActiveJobsOnly: Boolean
    $countryCode: String
  ) {
    searchHierarchicalLocations(
      searchTerm: $searchTerm
      levels: $levels
      limit: $limit
      withActiveJobsOnly: $withActiveJobsOnly
      countryCode: $countryCode
    ) {
      id
      CountryCode: countryCode
      name
      formattedName
      level
      parents {
        id
        name
        formattedName
        level
        CountryCode: countryCode
        parents {
          level
        }
      }
    }
  }
`;

export const hierarchicalJobLocationPreference = gql`
  query jobLocationPreferences {
    jobLocationPreferences {
      ...JobLocationPreferenceFields
    }
  }
  ${JobLocationPreferenceFields}
`;

export const getHierarchicalLocationById = gql`
  query getHierarchicalLocationById($id: String!) {
    location(id: $id) {
      id
      latitude
      longitude
      CountryCode: countryCode
      name
      administrativeLevelName
      formattedName
      parents {
        id
        name
        level
        administrativeLevelName
        formattedName
      }
    }
  }
`;

export const updateJobLocationPreferences = gql`
  mutation updateJobLocationPreferences(
    $addHierarchicalJobLocationPreferencesInput: [HierarchicalJobLocationPreferenceInput!]!
    $deleteJobLocationPreferencesIds: [UUID!]!
  ) {
    updateJobLocationPreferences(
      addHierarchicalJobLocationPreferencesInput: $addHierarchicalJobLocationPreferencesInput
      deleteJobLocationPreferencesIds: $deleteJobLocationPreferencesIds
    ) {
      ...JobLocationPreferenceFields
    }
  }
  ${JobLocationPreferenceFields}
`;
